import Decimal from 'decimal.js';
import { z, ZodObject, ZodRawShape, ZodTypeAny } from 'zod';
import { expectTypeOf } from 'expect-type';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { type ZodOpenAPIMetadata } from '@asteasolutions/zod-to-openapi';

export interface ZodOpenAPIInternalMetadata {
  refId?: string;
  extendedFrom?: {
    refId: string;
    schema: ZodObject<ZodRawShape>;
  };
}
export interface ZodOpenApiFullMetadata<T = any> {
  _internal?: ZodOpenAPIInternalMetadata;
  metadata?: ZodOpenAPIMetadata<T>;
}

declare module 'zod' {
  interface ZodTypeDef {
    openapi?: ZodOpenApiFullMetadata;
  }
  interface ZodType<
    Output = any,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  > {
    openapi<T extends ZodTypeAny>(
      this: T,
      metadata: Partial<ZodOpenAPIMetadata<z.input<T>>>
    ): T;
    openapi<T extends ZodTypeAny>(
      this: T,
      refId: string,
      metadata?: Partial<ZodOpenAPIMetadata<z.input<T>>>
    ): T;
  }
}

extendZodWithOpenApi(z);

export * from 'zod';
export { z };

// Only validates format NOT whether it's a valid date on the calendar: YYYY-MM-DD
export const SIMPLE_DATE_REGEX = new RegExp('^\\d{4}-\\d{2}-\\d{2}$');

/**
 * WARNING: This does not validate if date is a valid date, only that it is in the format YYYY-MM-DD
 * For use with string dates in the format YYYY-MM-DD stored as pg `date` column type
 * Reference libs/utils/src/datetime.ts for conversion functions
 */
export type CalendarDate = string & { __calendarDate: true };
export const zCalendarDate = () =>
  z
    .string()
    .regex(SIMPLE_DATE_REGEX)
    .refine((val): val is CalendarDate => {
      return true;
    });

type CalendarDateSchema = z.output<ReturnType<typeof zCalendarDate>>;
expectTypeOf<CalendarDateSchema>().toEqualTypeOf<CalendarDate>();

/**
 * @deprecated Use zCalendarDate instead
 */
export const zCalendarDateTrimmed = () =>
  z.string().transform((val, ctx) => {
    const dateString = val.trim().slice(0, 10) as CalendarDate;
    const test = SIMPLE_DATE_REGEX.test(dateString);
    if (!test) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid date format',
      });
      return z.NEVER;
    }
    return dateString;
  });

/**
 * @deprecated Use zCalendarDate instead
 */
export const zDate = () => z.date().or(z.string());

/**
 * @deprecated Use zCalendarDate instead
 */
export const zIsoDate = () => z.string().regex(SIMPLE_DATE_REGEX);

/**
 * @deprecated Use zCalendarDate instead
 */
export const zDateOnly = () => z.date().or(z.string().regex(SIMPLE_DATE_REGEX));

/**
 * For use with DbTimestamp... types
 */
export const zTimestamp = () => z.date().or(z.string());

/**
 * Boolean or coerce a string to a boolean. If the string is not a valid boolean, it will throw an error.
 * See: https://github.com/colinhacks/zod/issues/2985
 */
export const zCoercedBoolean = () =>
  z.boolean().or(
    z
      .string()
      .refine(
        (value) => {
          const lowerValue = value.toLowerCase();
          return lowerValue === 'true' || lowerValue === 'false';
        },
        {
          message:
            'Value must be boolean or a string representation of a boolean, e.g. true or false',
        }
      )
      .transform((value) => {
        const lowerValue = value.toLowerCase();
        return lowerValue === 'true';
      })
  );

// https://regex101.com/r/69RUbB/1
export const PHONE_NUMBER_REGEX =
  // Contains 4 capture groups: 1-3 digit country code, 3 digit area code, 3 digit prefix, 4 digit suffix
  new RegExp(
    '^\\s*(?:\\+?(\\d{1,3}))?[-. (]*(\\d{3})[-. )]*(\\d{3})[-. ]*(\\d{4})(?: *x(\\d+))?\\s*$'
  );
export const zPhoneNumber = ({
  stringRequiredError,
  regexMessage,
}: { stringRequiredError?: string; regexMessage?: string } = {}) =>
  z
    .string({ required_error: stringRequiredError })
    .regex(PHONE_NUMBER_REGEX, { message: regexMessage });

export const zDecimal = () => z.instanceof(Decimal);

export const zDecimalNullable = () => {
  return z
    .instanceof(Decimal)
    .or(z.string())
    .or(z.number())
    .nullable()
    .transform((val, ctx) => {
      if (val === null) {
        return val;
      }

      if (typeof val === 'string' && val.trim() === '') {
        return null;
      }

      return transformDecimal(val, ctx);
    });
};

export const zDecimalRequired = () => {
  return z
    .instanceof(Decimal)
    .or(z.string())
    .or(z.number())
    .transform(transformDecimal);
};

function transformDecimal(
  val: Decimal | string | number | null | undefined,
  ctx: z.RefinementCtx
) {
  if (val instanceof Decimal) {
    return val;
  }

  if (typeof val === 'string') {
    try {
      return new Decimal(val);
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ctx.path,
        message: `For ${ctx.path.join(
          '.'
        )} string '${val}' is not convertible to Decimal`,
      });
      return z.NEVER;
    }
  }

  if (typeof val === 'number') {
    return new Decimal(val);
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ctx.path,
    message: `For ${ctx.path.join(
      '.'
    )} expected a string, number or Decimal but received ${val}`,
  });

  return z.NEVER;
}
